import querystring from 'querystring';
import { Api } from '@core/types';
import { SessionStorage } from '@core/storage';

class ApiService {
  async rawRequest<T>(requestParams: Api.IRequest): Promise<T> {
    // Cast to an unknown to remove from our current type <T> (hacky ik, its to work with legacy apis)
    const response = (await this.request<T>(requestParams)) as unknown;

    return response as T;
  }

  async request<T>(request: Api.IRequest): Promise<Api.IBaseResponse<T>> {
    if (request.cacheKey) {
      const cachedValue = SessionStorage.get<Api.IBaseResponse<T>>(request.cacheKey);
      if (cachedValue) {
        return cachedValue as Api.IBaseResponse<T>;
      }
    }
    // Get the url
    const url = this.getUrl(request);

    // Headers
    const headers = new Headers({
      'Content-Type': 'application/json; charset=utf-8',
      Accept: 'application/json',
    });

    if (request.isMultipartFormData === true) {
      headers.delete('Content-Type');
    }

    if (request.bearerToken) {
      headers.append('authorization', `Bearer ${request.bearerToken}`);
    }

    // construct a request
    const requestOptions: RequestInit = {
      method: request.method,
      headers,
      mode: 'cors',
      cache: 'default',
      body: this.getBody(request, request.files),
    };

    let response: Response | null = null;
    try {
      // await the response
      response = await fetch(url, requestOptions);

      if (response == null) {
        throw new Error('API response is null');
      }

      const data = (await response.json()) as Api.IBaseResponse<T>;

      if (request.cacheKey) {
        SessionStorage.set(request.cacheKey, data);
      }

      return data;
    } catch (error) {
      console.error('API Error:', request.method, url, requestOptions);
      throw error;
    }
  }

  getUrl(request: Api.IRequest) {
    const controller = request.controller ? request.controller : 'packout';
    const baseUrl = request.baseUrl || process.env.REACT_APP_API_ENDPOINT;

    let url = `${baseUrl}/api/${controller}/${request.slug}`;

    if ((request.method === Api.Method.GET || request.method === Api.Method.DELETE) && request.params) {
      url += '?';
      url += querystring.stringify({ ...request.params });
    }

    return url;
  }

  private getBody(
    request: Api.IRequest,
    files?: File[] | FileList | null | [],
    secondaryFiles?: File[] | FileList | null | [],
  ): string | FormData | null {
    if (request.method === 'GET') {
      return null;
    }

    if (request.isMultipartFormData === true) {
      const formData = new FormData();

      formData.append('params', JSON.stringify(request.params));

      if (files && files.length > 0) {
        for (let index = 0; index < files.length; index++) {
          const file = files[index];
          formData.append(`files-${index}`, file);
        }
      }

      return formData;
    }

    return JSON.stringify(request.params);
  }
}

export default new ApiService();
